/**
 * useActionPermissions — React Query hooks dla autopilot granular permissions.
 * Backed by server/modules/autopilot/routes.ts (GET/PUT /sessions/:id/permissions)
 */
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";

export type PermissionMode = "auto" | "require_approval" | "disabled";
export type AutopilotAction =
  | "scaling_up"
  | "scaling_down"
  | "killing_ads"
  | "creating_new_ads"
  | "ai_generation"
  | "publishing_to_meta"
  | "budget_changes"
  | "audience_changes"
  | "creative_rotation"
  | "duplicate_winner";

export interface PermissionDecision {
  allowed: boolean;
  requiresApproval: boolean;
  mode: PermissionMode;
  reason: string;
}

export interface PermissionMatrix {
  sessionId: string;
  mode: string;
  permissions: Record<AutopilotAction, PermissionDecision>;
}

export function usePermissionMatrix(sessionId: string | null) {
  return useQuery<PermissionMatrix>({
    queryKey: ["autopilot-permissions", sessionId],
    queryFn: () => apiRequest("GET", `/api/autopilot/sessions/${sessionId}/permissions`),
    enabled: !!sessionId,
  });
}

export function useUpdatePermissions(sessionId: string) {
  return useMutation({
    mutationFn: (permissions: Partial<Record<AutopilotAction, PermissionMode>>) =>
      apiRequest("PUT", `/api/autopilot/sessions/${sessionId}/permissions`, { permissions }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["autopilot-permissions", sessionId] });
    },
  });
}
